/*
 * Turning one ledger fact into one sentence.
 *
 * THE CLIENT WRITES EVERY WORD, and this file is where that rule is cashed in.
 * The server sends a kind, its subjects and the old and new values; decision 402
 * keeps rendered copy out of the ledger's payload on purpose, so that a wording
 * fix is a deploy rather than a data migration, and direction §7 rules out
 * server-assembled English for the six non-English tier-1 locales.
 *
 * THE HONESTY RULES LIVE HERE BECAUSE THE WORDS DO:
 *
 *   - Decision 55: absence is INFERRED, never stated as an act. A registration
 *     that vanished from a roster reads "is no longer listed", never "withdrew" —
 *     the source did not say anyone withdrew, it simply stopped saying they were
 *     entered.
 *   - Decision 126: a transition is an OBSERVATION, never a commitment.
 *   - Decision 186: a DE strip is "assigned", never "moved" (stage 4a's kinds;
 *     the rule is recorded here so the next author meets it).
 *   - Decision 360: the DAY a deadline means is the venue-zone day of the instant
 *     minus twelve hours, and `deadlineDayLabel` is the one implementation. This
 *     file calls it rather than forming a second opinion — which is exactly what
 *     internal/alerts/schedule.go does on the Go side, pinned by a test to the
 *     same four instants.
 *
 * WHAT THIS RETURNS is a translation key plus its interpolation values, not a
 * string. The caller does the `t()`, which keeps this file free of any
 * i18n plumbing and makes it testable as pure data — the assertion is "this fact
 * chose that key with those values", which is the part that can be wrong.
 */

import type { TFunction } from "i18next";

import type { Alert } from "./api";
import { withTableLabel, type DeTableLabel } from "./deTable";
import {
  dateLocale,
  deadlineDayLabel,
  firstLast,
  notificationName,
  venueTime,
  type NotificationNameFormat,
} from "./format";

/** The ledger payload, as much of it as the copy reads. */
interface Payload {
  field?: string;
  old?: unknown;
  new?: unknown;
  source?: string;
  rung?: string;
  local_date?: string;
  prior_withdrawn_at?: string;

  // The day-of fields (decision 484). All optional, and each kind reads only
  // its own — the same posture the Go side takes for the same reason.
  pool?: number;
  strip?: string;
  starts_at?: string;
  table?: string;
  table_size?: number;
  place?: number;
  field_size?: number;
  tie?: boolean;
  counter?: string;
  inferred?: boolean;
}

export interface AlertSentence {
  /** A key under `notifications.kind`. */
  key: string;
  values: Record<string, string | number>;
  /**
   * True where the key uses i18next's ORDINAL plurals (`_ordinal_one` and
   * friends) and `values.count` is the number they select on.
   *
   * Carried as a flag rather than folded into `values`, because `ordinal` is an
   * i18next OPTION and not an interpolation: passing it as a value would put
   * the literal word into any sentence that happened to reference it, and
   * omitting it would select the cardinal plural — "21th".
   */
  ordinal?: boolean;
  /**
   * The DE table named from its size, where the payload carries one (decision
   * 951). `values.table` holds FTL's heading as printed, which is English
   * whatever the venue; the caller renders this label in the reader's language
   * and puts it in `values.table` instead (`withTableName`). Absent on a row
   * written before the size existed, which reads the heading.
   */
  table?: DeTableLabel;
}

/** The rung that counts down to the HARD close rather than the regular one. */
const FIELD_LATE = "late_registration_closes_at";

/**
 * The field whose value is an INSTANT rather than a bare calendar day.
 *
 * `event.tomorrow` carries one or the other depending on what the source
 * published (decision 108), and only this one may be read as a time.
 */
const FIELD_STARTS_AT = "starts_at";

function payloadOf(alert: Alert): Payload {
  const raw = alert.payload as unknown;
  if (raw && typeof raw === "object") return raw as Payload;
  return {};
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Choose the sentence for one alert.
 *
 * A SWITCH AND NOT A MAP, for the reason internal/alerts/supersession.go gives:
 * the whole vocabulary is visible in one place and a kind added without a case
 * falls to a stated default rather than to `undefined`.
 */
export function alertSentence(
  alert: Alert,
  nameFormat: NotificationNameFormat = "full",
): AlertSentence {
  const p = payloadOf(alert);
  const zone = alert.tournament?.timezone ?? null;

  const tournament = alert.tournament?.name ?? "";
  const event = alert.event?.name ?? "";
  // Display order flips to "First Last" (decision 358); the stored surname-first
  // string is a sort key and never shown. WHICH PART of it the reader sees is
  // their own account-wide setting (decision 535), and "full" is what every
  // caller got before the setting existed.
  //
  // PASSED IN RATHER THAN READ HERE, deliberately: this module is imported by
  // the service worker, which has no session, no API client and no i18next, so
  // a module-level global or a fetch would break the one property that keeps
  // the push vocabulary in one home (decision 488).
  //
  // A TEAM'S ALERT NAMES THE TEAM where it would name the fencer (decision
  // 717), as FTL spelled it: the fact is the team's, and the entry stands for
  // every tracked member's copy of it. The members lead the line through
  // `teamMembers` below.
  const fencer = alert.team
    ? alert.team.name
    : alert.fencer
      ? notificationName(alert.fencer.name, nameFormat)
      : "";

  const base = { tournament, event, fencer };

  switch (alert.kind) {
    case "deadline.approaching": {
      const day = deadlineDayLabel(asString(p.new), zone) ?? "";
      // WHICH deadline the rung counts down to comes from the payload's field,
      // which is what internal/alerts put it there for: the first three rungs
      // count to the regular close and the fourth to the hard close, and telling
      // a reader "registration closes" about the hard close would understate it.
      return {
        key: p.field === FIELD_LATE ? "deadlineApproachingLate" : "deadlineApproaching",
        values: { ...base, day },
      };
    }

    case "deadline.changed": {
      const day = deadlineDayLabel(asString(p.new), zone) ?? "";
      const oldDay = deadlineDayLabel(asString(p.old), zone);
      const late = p.field === FIELD_LATE;
      // OLD IS EXPLICITLY NULL FOR A FIRST PUBLICATION (decision 405), which is
      // the whole reason the payload writes it as null rather than omitting it:
      // "here is the deadline" and "the deadline moved" are different sentences,
      // and only the payload can say which happened.
      if (!oldDay) {
        return {
          key: late ? "lateDeadlinePublished" : "deadlinePublished",
          values: { ...base, day },
        };
      }
      return {
        key: late ? "lateDeadlineChanged" : "deadlineChanged",
        values: { ...base, day, oldDay },
      };
    }

    case "registration.new":
      // ONE SENTENCE FOR BOTH A FIRST ENTRY AND A RE-LISTING (decision 403):
      // "James is registered for Y-14 Épée" is the same true sentence either
      // way. The payload carries `prior_withdrawn_at` so a future build can word
      // a return differently without a data change; 403 explicitly leaves that
      // to "later", so this build does not.
      return { key: "registrationNew", values: base };

    case "registration.withdrawn":
      // Decision 55, and the reason this file exists. Never "withdrew".
      return { key: "registrationWithdrawn", values: base };

    case "registration.tier_placed":
      // The national split's first badge on a USA Fencing entry (migration
      // 0041). TWO KEYS, NOT ONE WITH THE TIER INTERPOLATED: the tier word
      // agrees with the sentence around it in several locales, so the
      // translator owns each sentence whole — the ordinal rule's reasoning.
      // `event` is the untiered event, USA Fencing's own, which is why the
      // sentence has to name the field itself.
      return {
        key: p.new === "elite" ? "registrationTierPlacedElite" : "registrationTierPlacedChallenger",
        values: base,
      };

    case "event.time_published": {
      const when = venueTime(asString(p.new), { ...(zone ? { timeZone: zone } : {}) }) ?? "";
      return { key: "eventTimePublished", values: { ...base, when } };
    }

    case "event.time_changed": {
      const when = venueTime(asString(p.new), { ...(zone ? { timeZone: zone } : {}) }) ?? "";
      const oldWhen = venueTime(asString(p.old), { ...(zone ? { timeZone: zone } : {}) }) ?? "";
      return { key: "eventTimeChanged", values: { ...base, when, oldWhen } };
    }

    case "tournament.cancelled":
      // The source rides in the attribution suffix rather than in this sentence,
      // because only FRED publishes cancellation and a reader deserves to know
      // who said so — see attributionSource below.
      return { key: "tournamentCancelled", values: base };

    case "event.tomorrow": {
      // A START TIME MAY NOT EXIST, and saying nothing is the honest answer.
      // National events publish times after the entry deadline (decision 108),
      // so the reminder goes out with the day alone rather than waiting for a
      // time — and it must not invent one, which is decision 55's posture
      // applied to a clock.
      //
      // THE PAYLOAD'S `field` IS WHAT DECIDES, not whether `new` happens to
      // parse. internal/alerts writes `starts_at` with an instant and
      // `event_date` with a bare "2026-10-03", and `new Date("2026-10-03")` is a
      // perfectly valid midnight UTC — which formats in Chicago as 7:00 PM the
      // PREVIOUS DAY. Testing parseability rather than the field is how a
      // reminder comes to state a time nobody published, on the wrong day. It is
      // the same date-only trap format.ts documents at length, reached from a
      // new direction.
      const when =
        p.field === FIELD_STARTS_AT
          ? venueTime(asString(p.new), { ...(zone ? { timeZone: zone } : {}) })
          : null;
      if (!when) return { key: "eventTomorrow", values: base };
      return { key: "eventTomorrowAt", values: { ...base, when } };
    }

    default: {
      // The day-of vocabulary, in its own function because it is fourteen kinds
      // to the planning regime's nine and they share nothing but `base`.
      const dayOf = dayOfSentence(alert, p, base);
      if (dayOf) return withTableLabel(dayOf, p);

      // A kind this build does not know. Still a real sentence with a real
      // destination, because the alternative is a blank row: an older client
      // reading a newer server should degrade to "something changed" rather than
      // to nothing at all.
      return { key: "unknown", values: base };
    }
  }
}

/**
 * The day-of half (decision 484), and the two things that make it different
 * from the planning kinds above.
 *
 * ORDINALS GO THROUGH i18next's `_ordinal_*` PLURALS rather than through a
 * reusable "21st" the sentence interpolates. Kevin's constraint was that the
 * place be handled correctly for translations, and a bare ordinal cannot be:
 * several tier-1 locales inflect it to agree with what follows, so the
 * translator has to own the whole sentence. It costs sixteen keys for `placed`
 * where a shared ordinal would have cost eight, and it is the same mechanism
 * `active.poolPlace_ordinal_*` already uses — one pattern, not two.
 *
 * COALESCED FORMS COME OFF `context`, and there is exactly ONE of them. The
 * archive was replayed before any of this was written: 7 of 160 alerts carry a
 * sibling at all, and after the suppressions only `de_table_posted +
 * pool_round_complete` recurs — six occurrences. So this file does not hold a
 * template for arbitrary combinations; it holds the combination that happens.
 */

/** The kinds whose presence in `context` changes the lead's sentence. */
function hasContext(alert: Alert, kind: string): boolean {
  return (alert.context ?? []).some((c) => c.kind === kind);
}

/** A context entry's payload, for the one combination that reads one. */
function contextPayload(alert: Alert, kind: string): Payload {
  const hit = (alert.context ?? []).find((c) => c.kind === kind);
  if (!hit) return {};
  const raw = hit.payload as unknown;
  if (raw && typeof raw === "object") return raw as Payload;
  return {};
}

function dayOfSentence(alert: Alert, p: Payload, base: Record<string, string>): AlertSentence | null {
  const zone = alert.tournament?.timezone ?? null;
  const at = (v: unknown) => venueTime(asString(v), { ...(zone ? { timeZone: zone } : {}) });
  const time = at(p.starts_at);
  const strip = asString(p.strip);
  const table = asString(p.table);
  // THE OPPONENT IS NOT COVERED BY THE NAME-FORMAT SETTING and always renders
  // in full (decision 536, Kevin's ruling). A reader knows their own fencers
  // and does not know the opponent, so "Kalina vs Marcus" reads as one name
  // they recognise and one they do not: shortening the second half would state
  // less about the only person on the line the reader needs help identifying.
  // A team's opponent is a team, shown as FTL spelled it (decision 717).
  const opponent = alert.opponent
    ? alert.team
      ? alert.opponent.name
      : firstLast(alert.opponent.name)
    : "";

  switch (alert.kind) {
    case "dayof.checked_in":
      return { key: "dayofCheckedIn", values: base };

    case "dayof.scratched":
      return { key: "dayofScratched", values: base };

    case "dayof.promoted":
      // The national split's Challenger -> Elite move (migration 0040). The
      // poller files it under the ELITE event, so `event` already names the tier
      // the fencer moved into -- FTL's own title, which is where the tier shows.
      //
      // USA Fencing's nightly read files the same kind under the UNTIERED event
      // (migration 0041), whose name says nothing about a field, so its sentence
      // names the field itself. Told apart by `source`, which only that writer
      // sets: both payloads read challenger -> elite.
      if (p.source === "usafencing") return { key: "dayofPromotedField", values: base };
      return { key: "dayofPromoted", values: base };

    case "dayof.pool_assigned": {
      // Four variants because BOTH facts are independently absent-able, and
      // decision 112's "posted but unassigned" is a real state rather than
      // missing data. No "yet": a strip may genuinely never be posted (127
      // measured a venue that posted none), so the word would be a promise the
      // source never made — which is the distinction Kevin drew against
      // decision 472's "no time yet" on Upcoming, where publication IS
      // expected.
      const pool = p.pool == null ? "" : String(p.pool);
      const values = { ...base, pool, strip: strip ?? "", time: time ?? "" };
      if (strip && time) return { key: "dayofPoolAssigned", values };
      if (strip) return { key: "dayofPoolAssignedNoTime", values };
      if (time) return { key: "dayofPoolAssignedNoStrip", values };
      return { key: "dayofPoolAssignedBare", values };
    }

    case "dayof.pool_strip_moved":
      // THE ONE KIND THAT MAY SAY "MOVED" (decision 117): pools carry identity
      // across reads, so this is a change we observed rather than an inference.
      // The FROM strip is deliberately not spoken — Kevin's edit — though the
      // payload still records it: what a reader needs is where to go now.
      return {
        key: "dayofPoolStripMoved",
        values: { ...base, pool: p.pool == null ? "" : String(p.pool), to: asString(p.new) ?? "" },
      };

    case "dayof.pool_complete":
      return { key: "dayofPoolComplete", values: base };

    case "dayof.de_strip_assigned":
      // ASSIGNED, NEVER MOVED (decision 186). The table is spoken only where
      // there is no opponent to name: with both, Kevin's ruling drops it to keep
      // the line short, and de_table_posted is the kind that owns that fact.
      if (opponent) {
        const values = { ...base, strip: strip ?? "", opponent, time: time ?? "" };
        return { key: time ? "dayofDeStripVs" : "dayofDeStripVsNoTime", values };
      }
      return {
        key: table
          ? time
            ? "dayofDeStripAssigned"
            : "dayofDeStripAssignedNoTime"
          : time
            ? "dayofDeStripAssignedNoTable"
            : "dayofDeStripAssignedBare",
        values: { ...base, strip: strip ?? "", table: table ?? "", time: time ?? "" },
      };

    case "dayof.de_opponent_assigned": {
      // The no-strips case: at a venue that posts no DE strip queue, the
      // opponent is the whole of what can be said about a fencer's next bout
      // (decision 127 measured one venue that populated none of its 13 rounds).
      const values = { ...base, opponent, table: table ?? "" };
      return { key: table ? "dayofDeOpponent" : "dayofDeOpponentNoTable", values };
    }

    case "dayof.de_table_posted": {
      // The one coalesced form the archive actually produces, six times: the
      // tableau going up in the same cycle as the pool round closing. Two
      // strengths of evidence, so two sentences — the counter said so, or it was
      // inferred from the next round starting (decision 126).
      const values = { ...base, table: table ?? "" };
      if (hasContext(alert, "dayof.pool_round_complete")) {
        const ctx = contextPayload(alert, "dayof.pool_round_complete");
        return {
          key: ctx.inferred ? "dayofDeTableWithPoolsInferred" : "dayofDeTableWithPools",
          values,
        };
      }
      // THERE IS NO BYE SENTENCE HERE, and its removal is the decision rather
      // than an omission (stage 4a PR 2, Kevin's ruling). Decision 485 shipped
      // one `dayofDeTablePostedBye` reported-as-unreachable: decision 457
      // derives a bye at READ time from the pool standings and the table size,
      // and the BRACKET payload -- which is what this kind is written from --
      // cannot tell a bye from a feeder bout nobody has fenced. So nothing could
      // ever set the flag, and the whole chain behind it came out with the
      // string: `Payload.Bye` in Go, `DayOfFact.Bye`, and the parameter every
      // caller passed `false` to. Active's bye is untouched and unrelated; it is
      // computed from data this path does not have.
      return { key: "dayofDeTablePosted", values };
    }

    case "dayof.de_table_updated":
      // "IS NOW IN" AND NOT "ADVANCED TO" (decision 126, Kevin's ruling on the
      // copy): a bracket position is what we observed, and the advance is what
      // it means. The inference is sound and it is still an inference.
      return { key: "dayofDeTableUpdated", values: { ...base, table: table ?? "" } };

    case "dayof.pool_round_complete":
      return {
        key: p.inferred ? "dayofPoolRoundCompleteInferred" : "dayofPoolRoundComplete",
        values: base,
      };

    case "dayof.placed": {
      // The denominator may be absent, and that is a real state rather than
      // missing data: a payload can carry a place with no field to count it
      // against. Decision 442's drift — "21st of 23", then "of 31", then "of
      // 39" — is handled by the LEDGER rather than here: each revision is its
      // own row and supersession shows the reader the newest, so this renders
      // whichever one it is handed.
      const place = typeof p.place === "number" ? p.place : 0;
      const field = typeof p.field_size === "number" ? String(p.field_size) : null;
      const values = { ...base, count: place, field: field ?? "" };
      const key = field
        ? p.tie
          ? "dayofPlacedTie"
          : "dayofPlaced"
        : p.tie
          ? "dayofPlacedNoFieldTie"
          : "dayofPlacedNoField";
      return { key, values, ordinal: true };
    }

    case "dayof.eliminated":
      return { key: "dayofEliminated", values: { ...base, table: table ?? "" } };

    case "dayof.event_finished":
      // ONE KEY AND NO COMPETITOR COUNT (Kevin's edit). The count was in the
      // shadow log's line and says nothing a reader of a finished event needs.
      return { key: "dayofEventFinished", values: base };

    case "dayof.start_moved": {
      const oldWhen = at(p.old);
      const values = { ...base, time: time ?? "", oldTime: oldWhen ?? "" };
      return { key: oldWhen ? "dayofStartMoved" : "dayofStartMovedNoOld", values };
    }

    default:
      return null;
  }
}


/**
 * Which source to attribute a line to, or null for none.
 *
 * ONLY WHERE THE SOURCE IS PART OF THE FACT. The design document's copy
 * attributes exactly two kinds — "Trick or Retreat has been cancelled (AskFRED)"
 * and "James is registered for Y-14 Épée at Trick or Retreat (USA Fencing)" —
 * because for both of them WHO SAID SO is what a reader would ask next. A
 * deadline rung has no source at all (nothing observed it; internal/alerts
 * leaves the field empty on the clock-detected kinds, deliberately, as the way
 * to tell the two detection methods apart), and an event time is a fact about
 * the schedule rather than about a source's opinion.
 */
export function attributionSource(alert: Alert): string | null {
  const p = payloadOf(alert);
  const source = asString(p.source);
  if (!source) return null;

  switch (alert.kind) {
    case "tournament.cancelled":
    case "registration.new":
    case "registration.withdrawn":
      return source;
    default:
      return null;
  }
}

/**
 * A team alert's tracked members, in the reader's name format and joined, or
 * null for a person's alert (decision 717). The feed leads a team's line with
 * them, as the push leads its body: the sentence names the team, and these
 * are who make it this reader's business.
 *
 * The separator is the caller's, from the catalogue's `team.nameSeparator`:
 * this module has no i18next (the service worker imports it), and the
 * separator is the language's -- "、" in Chinese, "، " in Arabic.
 */
export function teamMembers(
  alert: Alert,
  nameFormat: NotificationNameFormat = "full",
  separator = ", ",
): string | null {
  const members = alert.team?.members ?? [];
  if (members.length === 0) return null;
  return members.map((m) => notificationName(m, nameFormat)).join(separator);
}

/** The day heading for a group, from the fact of how many days ago it was. */
export function dayHeading(daysAgo: number, dateISO: string, t: TFunction, locale = dateLocale()): string {
  if (daysAgo <= 0) return t("notifications.day.today");
  if (daysAgo === 1) return t("notifications.day.yesterday");
  // Older than yesterday gets a date. `dateISO` is already a calendar day in the
  // device zone, so it is formatted as a date-only value — never as an instant,
  // which is the off-by-one trap format.ts documents at length.
  return (
    new Intl.DateTimeFormat(locale, {
      timeZone: "UTC",
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(new Date(`${dateISO}T00:00:00Z`)) ?? dateISO
  );
}
